import { memo, useCallback, useState } from 'react';

import { motion } from 'motion/react';
import Path from 'path-to-regexp';
import { useHistory } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import { Skeleton } from '@/shadcn/skeleton';

import { MAX_URL_TRACE_IDS } from '@/constant/batch';
import { ROUTE_NEW_LINK } from '@/constant/routes';
import { Empty } from '../empty/empty';
import type { NextAction, Tasks } from './actionList.types';
import {
  GroupActionsList,
  type Link,
  type PrioritySettings
} from './groupActionsList';

/**
 * Optional batch-action wiring (STMN-3358/3359). Supplied by the orchestrator
 * ONLY when the `USE_BATCH_ACTION` flag is on and this is the multi-trace update
 * path. When absent (new-trace tray, single-trace, flag off), `ActionsList` keeps
 * its original navigate-to-newLink behavior untouched.
 */
export type BatchActionHooks = {
  /** Run the selected action as a server-side batch over the explicit trace set. */
  onPerformBatch: (link: Link, traceIds: string[]) => void;
  /** True when a filter is active on the overview (enables the "apply to all" banner). */
  hasActiveFilter: boolean;
  /** Count of traces matching the active filter (all matching, not just the page). */
  matchingCount: number;
  /** Run the selected action over every filter-matching trace (filter-mode batch). */
  onApplyToAllMatching: (link: Link) => void;
  /**
   * True when the tray was opened in "filter-scan mode" — 0 rows checked, filter
   * active, actions computed from the visible page. Lets the empty-state fall
   * through to "no common action" instead of "no trace selected".
   */
  isFilterScanMode?: boolean;
};

type Props = {
  workflowId: string;
  nextActions?: NextAction[];
  tasks?: Tasks | null;
  traceIds?: string[];
  toggleTray?: () => void;
  loading?: boolean;
  canSelect?: boolean;
  batch?: BatchActionHooks;
};

export const ActionsList = memo(
  ({
    workflowId,
    nextActions = [],
    tasks = null,
    traceIds = [],
    toggleTray = () => {},
    loading = false,
    canSelect = false,
    batch
  }: Props) => {
    const history = useHistory();
    const [selectedLink, setSelectedLink] = useState<Partial<Link>>({});

    const isCompleteLink = (link: Partial<Link>): link is Link =>
      !!(link.groupKey && link.actionKey);

    // A batch action carries its selection out of band, so it has no size limit.
    // The pre-batch path enumerates the ids into the newLink query string, which
    // the ingress 414s past ~215 of them — caught here, while the tray is still
    // open, rather than on a dead page.
    const overSelectionLimit = !batch && traceIds.length > MAX_URL_TRACE_IDS;

    const actionsHandler = useCallback(() => {
      if (overSelectionLimit) {
        return;
      }

      const { groupKey, actionKey } = selectedLink;

      // New path (STMN-3359): run a server-side batch action instead of the
      // per-trace init + client-signing navigation. Only taken when the
      // orchestrator wired `batch` (flag on + multi-trace update path).
      if (batch && isCompleteLink(selectedLink)) {
        batch.onPerformBatch(selectedLink, traceIds);
        return;
      }

      const baseUrl = Path.compile(ROUTE_NEW_LINK)({
        wfid: workflowId
      });
      return history.push(
        `${baseUrl}?groupKey=${groupKey}&actionKey=${actionKey}${
          traceIds !== null ? `&traceIds=${traceIds.join(',')}` : ''
        }`,
        {
          from: history.location,
          goToTraceInspector: traceIds.length === 0
        }
      );
    }, [
      selectedLink,
      workflowId,
      history,
      traceIds,
      batch,
      overSelectionLimit
    ]);

    const usedNextActions = nextActions || [];

    const getPrioritySettings = (
      nextAction: NextAction
    ): PrioritySettings | undefined => {
      if (!tasks?.groups) return;
      const todos = tasks.groups?.[nextAction.group?.label]?.todo;
      if (!todos?.length) return;

      return {
        priorityActionKey: todos,
        priorityGroupLabel: nextAction.group.label // if we are here, then this label is in the tasks groups
      };
    };

    const SingleActionSkeleton = () => (
      <div className="flex flex-row items-center gap-2">
        <Skeleton className="aspect-square w-5 rounded-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );

    const ActionListSkeleton = () => (
      <div className="flex flex-col gap-2 pr-4">
        <div className="flex h-[40px] flex-row items-center gap-3 pt-4 pl-2">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <Skeleton className="h-5 w-full" />
        </div>
        <div className="flex flex-col gap-2 pt-2 pl-[53px]">
          <SingleActionSkeleton />
          <SingleActionSkeleton />
        </div>
      </div>
    );

    // Filter-scan mode replaces the "no trace selected" empty state with the
    // "no common action" one, since the tray is populated from the visible
    // page rather than a manual selection.
    const inFilterScan = !!batch?.isFilterScanMode;
    const isEmpty =
      !loading &&
      (traceIds.length > 0 || inFilterScan) &&
      nextActions.length === 0;
    const nextActionsEmpty = !loading && nextActions.length === 0;
    const nothingSelected =
      canSelect &&
      !loading &&
      !inFilterScan &&
      traceIds.length === 0 &&
      nextActions.length === 0;
    const selectionLimitMessage = `Select at most ${MAX_URL_TRACE_IDS} traces to run this action (${traceIds.length} selected).`;

    const EmptyComponent = ({ message }: { message: string }) => (
      <div className="flex h-2/3 flex-col items-center justify-center">
        <Empty className="size-[100px]" message={message} />
      </div>
    );

    function calculateDelay(nextAction: NextAction[]) {
      const total = nextAction.reduce(
        (acc, action) => acc + action.actions.length,
        0
      );

      if (total < 10) return 0.1;

      if (total < 20) return 0.05;

      return 0.03;
    }

    const delay = calculateDelay(usedNextActions);

    return (
      <div className="flex size-full flex-col justify-between overflow-auto transition-all">
        <div className="scrollbar-thin w-full grow overflow-auto">
          {/* we keep the previous next actions while loading the new ones */}
          {loading && !nextActionsEmpty && <ActionListSkeleton />}
          {!loading &&
            usedNextActions.map((nextAction, index) => (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 10 }}
                key={nextAction.group.label}
                transition={{
                  duration: 0.2,
                  delay: 0.05 + delay * index
                }}
              >
                <GroupActionsList
                  groupActions={nextAction}
                  key={nextAction.group.label}
                  priority={getPrioritySettings(nextAction)}
                  selectedLink={selectedLink}
                  setSelectedLink={setSelectedLink}
                />
              </motion.div>
            ))}
          {isEmpty && <EmptyComponent message="No actions available" />}
          {nothingSelected && (
            <EmptyComponent message="No trace selected, please select a trace to perform an action" />
          )}
        </div>
        <footer className="mt-2 flex shrink-0 flex-col items-end gap-2">
          {overSelectionLimit && (
            <p
              className="text-destructive text-sm"
              data-cy="selection-limit-warning"
            >
              {selectionLimitMessage}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              data-cy="cancel-button"
              onClick={() => toggleTray()}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              data-cy="perform-next-action-button"
              disabled={
                Object.keys(selectedLink).length === 0 || overSelectionLimit
              }
              onClick={() => actionsHandler()}
            >
              Perform action
            </Button>
          </div>
        </footer>
      </div>
    );
  }
);
